import React from 'react';
import { Modal } from './Modal';

import styled from 'styled-components';
import { Body, Heading3, LinkMixin } from '@qover/typography';
import { PrimaryButton } from '@qover/molecules';
import { Trans } from '@lingui/macro';

import { StripeIcon } from './icons/Stripe';
import { TwikeyIcon } from './icons/Twikey';
import { PaymentMethod } from '@qover-private/protos-web/qover/dojo/payment/v1/payment_method';

const CloseButton = styled.button`
  ${LinkMixin};
  position: absolute;
  right: 15px;
  top: 15px;
`;

const LogosContainer = styled.div`
  display: flex;
  justify-content: space-around;
  flex-direction: row;
  margin-top: 40px;

  svg.stripe {
    width: 100%;
    height: 40px;
  }

  svg.twikey {
    width: 100%;
    height: 60px;
  }

  @media screen and (max-width: ${({ theme }) =>
      theme.breakpoints.maxWidth.S}) {
    flex-direction: column;
    margin-top: 0px;
  }
`;

const BackgroundImg = styled.a`
  max-width: 200px;
  width: 100%;
  height: 80px;
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 ${({ theme }) => theme.colors.textQuote(0.15)};
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: ${({ theme }) =>
      theme.breakpoints.minWidth.S}) {
    max-width: 100%;
    width: 100%;
    height: 80px;
    margin-top: 40px;
  }
`;

const StyledModal = styled(Modal)`
  max-width: 700px;
  width: 100%;
  padding: 80px;

  @media screen and (max-width: ${({ theme }) =>
      theme.breakpoints.minWidth.S}) {
    max-width: 100%;
    width: 100%;
    max-height: 100%;
    height: 100%;
    padding: 20px 20px 20px 20px;
  }
`;

const StyledBottom = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 55px;
`;

type PaymentInfoModalProps = {
  onClose: () => void;
  hideOverflow?: boolean;
  closeOnClickOutside?: boolean;
  paymentMethods: PaymentMethod[];
};

export function PaymentInfoModal(props: PaymentInfoModalProps) {
  const {
    closeOnClickOutside = true,
    onClose,
    hideOverflow,
    paymentMethods,
  } = props;

  const hasStripe = paymentMethods.some(
    method =>
      method === PaymentMethod.PAYMENT_METHOD_BANCONTACT ||
      method === PaymentMethod.PAYMENT_METHOD_CREDITCARD ||
      method === PaymentMethod.PAYMENT_METHOD_GIROPAY ||
      method === PaymentMethod.PAYMENT_METHOD_IDEAL ||
      method === PaymentMethod.PAYMENT_METHOD_SOFORT,
  );

  const hasTwikey = paymentMethods.some(
    method => method === PaymentMethod.PAYMENT_METHOD_SEPADD,
  );

  return (
    <StyledModal
      hideOverflow={hideOverflow}
      onClickOutside={closeOnClickOutside ? onClose : () => undefined}
    >
      <CloseButton onClick={onClose}>
        <Trans id="general.paymentModal.closeLink" />
      </CloseButton>
      <Heading3>
        <Trans id="general.paymentModal.title" />
      </Heading3>
      <Body>
        <Trans id="general.paymentModal.description" />
      </Body>
      <LogosContainer>
        {hasStripe && (
          <BackgroundImg
            href="https://stripe.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <StripeIcon className="stripe" />
          </BackgroundImg>
        )}
        {hasTwikey && (
          <BackgroundImg
            href="https://www.twikey.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <TwikeyIcon className="twikey" />
          </BackgroundImg>
        )}
      </LogosContainer>
      <StyledBottom>
        <PrimaryButton onClick={props.onClose}>
          <Trans id="general.paymentModal.closeButton" defaults="Fermer" />
        </PrimaryButton>
      </StyledBottom>
    </StyledModal>
  );
}
